// WbYardsPage — /weighbridge/settings/yards (admin only).
//
// Phase C2 Group 1. Lists the tenant's yards from /api/v1/wb/yards. Row
// click navigates to the detail/edit page; "+ Add Yard" routes to /new.

import { useNavigate } from 'react-router-dom';
import { Plus, Warehouse } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Badge } from '../../../components/ui/Badge/Badge';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { useWbYards } from '../hooks/useWbYards';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbYardsPage.module.css';

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

export function WbYardsPage(): JSX.Element {
  const navigate = useNavigate();
  const yardsQuery = useWbYards();
  const yards = yardsQuery.data?.yards ?? [];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Yards"
        subtitle="Physical sites hosting one or more weighbridges"
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/weighbridge/settings/yards/new')}
          >
            Add Yard
          </Button>
        }
      />

      {yardsQuery.isError ? (
        <p className={styles.error}>{extractErrorMessage(yardsQuery.error)}</p>
      ) : null}

      <Card flush>
        {yardsQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading yards" />
          </div>
        ) : yards.length === 0 ? (
          <EmptyState
            icon={Warehouse}
            title="No yards yet"
            description="Add a yard to start onboarding weighbridges."
            action={
              <Button
                variant="primary"
                icon={Plus}
                onClick={() => navigate('/weighbridge/settings/yards/new')}
              >
                Add Yard
              </Button>
            }
          />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Address</th>
                <th>Timezone</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {yards.map((y) => (
                <tr
                  key={y.enc_id}
                  onClick={() => navigate(`/weighbridge/settings/yards/${y.enc_id}`)}
                >
                  <td>{y.name}</td>
                  <td className={styles.codeCell}>{y.code}</td>
                  <td>
                    {y.address ? (
                      truncate(y.address, 60)
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                  <td>{y.timezone}</td>
                  <td>
                    {y.is_active ? (
                      <Badge variant="success">Active</Badge>
                    ) : (
                      <Badge variant="muted">Inactive</Badge>
                    )}
                  </td>
                  <td className={styles.actionsCell}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/weighbridge/settings/yards/${y.enc_id}`);
                      }}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
